import { useEffect, useState } from 'react'

/** Read-only ref whose value is created on first access. */
export interface LazyRef<T> {
  readonly current: T
}

class OwnedAudioContext implements LazyRef<AudioContext> {
  private context: AudioContext | null = null
  private isReleased = false

  get current(): AudioContext {
    if (!this.context) {
      this.context = new AudioContext()
      // A late async continuation after unmount must not leave a live context behind.
      if (this.isReleased) void this.context.close().catch(() => undefined)
    }
    return this.context
  }

  release(): void {
    this.isReleased = true
    if (this.context && this.context.state !== 'closed') void this.context.close().catch(() => undefined)
  }
}

class LazyValue<T> implements LazyRef<T> {
  private value: T | undefined

  constructor(private readonly create: () => T) {}

  get current(): T {
    if (this.value === undefined) this.value = this.create()
    return this.value
  }
}

/**
 * One AudioContext per component instance, created on first use (never during render)
 * and closed on unmount.
 *
 * `useRef(new AudioContext())` evaluated its argument on every render, and StrictMode
 * discards a render in development, so each render leaked a running context. Chrome
 * caps live audio output streams (~50 on macOS); past the cap new contexts never
 * render, which silently flattened the stethoscope waveform after leaving and
 * re-entering a consultation.
 */
export function useOwnedAudioContext(): LazyRef<AudioContext> {
  const [owner] = useState<OwnedAudioContext>(() => new OwnedAudioContext())

  useEffect(() => () => owner.release(), [owner])

  return owner
}

/** An audio node of an owned context, created on first use rather than during render. */
export function useLazyAudioNode<T extends AudioNode>(audioContextRef: LazyRef<AudioContext>, create: (audioContext: AudioContext) => T): LazyRef<T> {
  const [node] = useState<LazyValue<T>>(() => new LazyValue<T>(() => create(audioContextRef.current)))
  return node
}
